<template>
  <div class="space-y-5">
    <h2 class="mb-5 text-center text-lg font-medium text-[var(--plugin-charcoal)]">
      {{ t('plugin.summary.title') }}
    </h2>

    <p
      v-if="submitError"
      class="rounded-[8px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
    >
      {{ submitError }}
    </p>
    <div
      v-else-if="state.submitted"
      class="rounded-[8px] border border-green-200 bg-green-50 px-4 py-3 text-center text-green-800"
    >
      {{ t('plugin.notifications.reservationCreated') }}
    </div>

    <div class="flex items-start gap-4 rounded-[8px] border border-[var(--plugin-border)] p-4">
      <div class="flex h-[50px] w-[50px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-[var(--plugin-gray-100)]">
        <img
          v-if="branchImageUrl && !branchImageFailed"
          :src="branchImageUrl"
          alt=""
          class="h-full w-full object-cover"
          @error="branchImageFailed = true"
        />
        <svg
          v-else
          class="h-6 w-6 text-[var(--plugin-gray-400)]"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"
          />
        </svg>
      </div>
      <div class="min-w-0 flex-1">
        <p class="font-semibold text-[var(--plugin-charcoal)]">{{ state.branch?.name }}</p>
        <p class="mt-1.5 text-sm text-[var(--plugin-gray-500)]">{{ branchAddress }}</p>
      </div>
    </div>

    <div class="flex justify-between gap-4">
      <p class="text-sm font-medium text-[var(--plugin-gray-500)]">{{ t('plugin.summary.employee') }}</p>
      <p class="text-sm font-medium text-[var(--plugin-gray-500)]">{{ t('plugin.summary.dateTime') }}</p>
    </div>
    <div class="flex justify-between gap-4">
      <p class="text-[var(--plugin-charcoal)]">{{ employeeDisplayName }}</p>
      <p class="text-[var(--plugin-charcoal)]">{{ dateTimeDisplay }}</p>
    </div>

    <div class="flex flex-wrap items-start justify-between gap-4">
      <div class="min-w-0">
        <p class="font-medium text-primary">{{ state.product?.name }}</p>
        <p class="mt-2 text-sm text-[var(--plugin-charcoal)]">{{ durationFormatted }}</p>
      </div>
      <p v-if="showPrice" class="text-lg font-semibold text-primary">{{ priceDisplay }} €</p>
    </div>

    <div class="border-t border-[var(--plugin-border)] pt-4" />

    <template v-if="showPaymentSection">
      <div class="space-y-3">
        <p class="text-sm font-medium text-[var(--plugin-gray-500)]">{{ t('plugin.summary.paymentMethod') }}</p>
        <div class="flex flex-wrap gap-4">
          <label
            v-for="method in enabledPaymentMethods"
            :key="method.id"
            class="flex cursor-pointer items-center gap-2"
          >
            <input
              type="radio"
              :value="method.id"
              :checked="state.selectedPaymentMethodId === method.id"
              name="payment-method"
              class="h-4 w-4 border-[var(--plugin-border)] text-primary focus:ring-primary"
              @change="setSelectedPaymentMethodId(method.id)"
            />
            <span
              class="text-sm"
              :class="state.selectedPaymentMethodId === method.id ? 'font-medium text-primary' : 'text-[var(--plugin-charcoal)]'"
            >
              {{ method.name }}
            </span>
          </label>
        </div>
        <p v-if="requiredPayment || isSelectedCardMethod" class="mt-1 text-xs text-[var(--plugin-charcoal)]">
          {{ t('plugin.summary.requiredPaymentCardNote') }}
        </p>
        <p v-else-if="facilityName && isSelectedCashMethod" class="mt-1 text-xs text-[var(--plugin-charcoal)]">
          {{ t('plugin.summary.requiredPaymentNote').replace('{facilityName}', facilityName) }}
        </p>
      </div>
      <div class="border-t border-[var(--plugin-border)] pt-4" />
    </template>

    <div class="flex flex-wrap items-center justify-between gap-4">
      <p class="text-sm font-medium text-[var(--plugin-charcoal)]">{{ t('plugin.summary.label') }}</p>
      <div class="flex items-center gap-2">
        <span class="text-sm text-[var(--plugin-charcoal)]">{{ durationFormatted }}</span>
        <span v-if="showPrice" class="font-semibold text-primary">{{ priceDisplay }} €</span>
      </div>
    </div>

    <div class="space-y-3">
      <label class="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          :checked="state.termsAgreement"
          class="mt-1 h-4 w-4 rounded border-[var(--plugin-border)] text-primary focus:ring-primary"
          @change="setTermsAgreement(($event.target as HTMLInputElement).checked)"
        />
        <span class="text-sm font-light text-[var(--plugin-charcoal)]">
          {{ t('plugin.summary.agreeWith') }}
          <a
            :href="termsUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-primary font-semibold no-underline"
            @click.stop
          >{{ t('plugin.summary.termsLink') }}</a>
        </span>
      </label>
      <label class="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          :checked="state.privacyAgreement"
          class="mt-1 h-4 w-4 rounded border-[var(--plugin-border)] text-primary focus:ring-primary"
          @change="setPrivacyAgreement(($event.target as HTMLInputElement).checked)"
        />
        <span class="text-sm font-light text-[var(--plugin-charcoal)]">
          {{ t('plugin.summary.agreeProcessingOf') }}
          <a
            :href="privacyUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-primary font-semibold no-underline"
            @click.stop
          >{{ t('plugin.summary.privacyLink') }}</a>
        </span>
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useWizardState, setSelectedPaymentMethodId, setTermsAgreement, setPrivacyAgreement } from '@/composables/useWizardState'
import { formatDurationMinutes } from '@/utils/durationFormat'
import { t } from '@/i18n'
import { isCardPaymentName, isCashPaymentName } from '@/utils/paymentMethods'
import type { PublicBranch, PublicReservationProduct, PublicEmployee } from '@/types/api'

const props = withDefaults(
  defineProps<{
    paymentMethods?: Array<{ id: number; name: string }>
    enabledPaymentMethodIds?: number[]
    requiredPayment?: boolean
    facilityName?: string
    facilitySlug?: string
    isZlavaDna?: boolean
    submitError?: string | null
  }>(),
  {
    paymentMethods: () => [],
    enabledPaymentMethodIds: () => [],
    requiredPayment: false,
    facilityName: '',
    facilitySlug: '',
    isZlavaDna: false,
    submitError: null,
  }
)

const termsUrl = computed(() => {
  const slug = props.facilitySlug ? encodeURIComponent(props.facilitySlug) : ''
  return slug ? `${window.location.origin}/${slug}/obchodne-podmienky` : '#'
})

const privacyUrl = computed(() => {
  const slug = props.facilitySlug ? encodeURIComponent(props.facilitySlug) : ''
  return slug ? `${window.location.origin}/${slug}/suhlas-so-spracovanim-osobnych-udajov` : '#'
})

const state = useWizardState()
const branchImageFailed = ref(false)

const branchImageUrl = computed(() => {
  const branch = state.branch as (PublicBranch & { media?: Array<{ url: string }> }) | null
  return branch?.media?.[0]?.url ?? null
})

const branchAddress = computed(() => state.branch?.address ?? '—')

const employeeDisplayName = computed(() => {
  const e = state.employee as PublicEmployee | null
  if (!e) return t('plugin.summary.anyEmployee')
  return e.surname ? `${e.name} ${e.surname}` : e.name
})

const dateTimeDisplay = computed(() => {
  const d = state.selectedDate
  const timeVal = state.selectedTime
  if (!d || !timeVal) return '—'
  const timeStr = typeof timeVal === 'string' && timeVal.length >= 5 ? timeVal.slice(0, 5) : timeVal
  return `${d} ${timeStr}`
})

const productDurationMinutes = computed(() => {
  const p = state.product as (PublicReservationProduct & { duration_minutes?: number }) | null
  return p?.duration_minutes ?? p?.duration ?? 0
})

const durationFormatted = computed(() => formatDurationMinutes(productDurationMinutes.value))

const productPrice = computed(() => {
  const p = state.product as PublicReservationProduct | null
  return p?.price ?? 0
})

const priceDisplay = computed(() => String(productPrice.value))

const showPrice = computed(() => !props.isZlavaDna)

const showPaymentSection = computed(
  () =>
    !props.isZlavaDna &&
    (props.requiredPayment ||
      (props.enabledPaymentMethodIds?.length ?? 0) > 0 ||
      (props.paymentMethods?.length ?? 0) > 0)
)

const rawPaymentMethods = computed(() => {
  const ids = props.enabledPaymentMethodIds ?? []
  const list = props.paymentMethods ?? []
  return ids.length === 0 ? list : list.filter((m) => ids.includes(m.id))
})

const cardMethodIds = computed(() => {
  const set = new Set<number>()
  for (const method of rawPaymentMethods.value) {
    if (isCardPaymentName(method.name)) {
      set.add(method.id)
    }
  }
  return set
})

const cashMethodIds = computed(() => {
  const set = new Set<number>()
  for (const method of rawPaymentMethods.value) {
    if (isCashPaymentName(method.name)) {
      set.add(method.id)
    }
  }
  return set
})

const enabledPaymentMethods = computed(() => {
  const methods = rawPaymentMethods.value
  if (props.requiredPayment) {
    const cardOnly = methods.filter((m) => cardMethodIds.value.has(m.id))
    const list = cardOnly.length > 0 ? cardOnly : methods.slice(0, 1)
    return list.map((m) => ({ id: m.id, name: t('plugin.summary.paymentCardOnline') }))
  }
  if (methods.length > 0) {
    return methods.map((m) =>
      cardMethodIds.value.has(m.id)
        ? { ...m, name: t('plugin.summary.paymentCardOnline') }
        : m
    )
  }
  const ids = props.enabledPaymentMethodIds ?? []
  const firstId = ids[0]
  if (firstId != null && ids.length > 0) {
    return [{ id: firstId, name: t('plugin.summary.paymentCardOnline') }]
  }
  return []
})

const isSelectedCardMethod = computed(() => {
  const selectedId = state.selectedPaymentMethodId
  if (selectedId == null) return false
  return cardMethodIds.value.has(selectedId)
})

const isSelectedCashMethod = computed(() => {
  const selectedId = state.selectedPaymentMethodId
  if (selectedId == null) return false
  return cashMethodIds.value.has(selectedId)
})

watch(
  () => state.branch?.id,
  () => { branchImageFailed.value = false },
  { immediate: true }
)

watch(
  () => state.branch?.id,
  () => { branchImageFailed.value = false },
  { immediate: true }
)

watch(
  () => [state.step, props.requiredPayment, enabledPaymentMethods.value, state.selectedPaymentMethodId] as const,
  ([step, required, methods]) => {
    if (step !== 'summary' || !required || methods.length === 0) return
    if (state.selectedPaymentMethodId != null) return
    const first = methods[0]
    if (first) setSelectedPaymentMethodId(first.id)
  },
  { immediate: true }
)
</script>
